import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Users, Inbox, FileText, Briefcase, Star, TrendingUp, MessageCircle, Sparkles } from "lucide-react";

const KPI = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 lift-on-hover" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="mt-4 text-3xl font-heading font-bold text-[#2D3748]">{value}</div>
    <div className="text-sm text-[#718096] font-sans">{label}</div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [aiOn, setAiOn] = useState(false);
  const [twilioOn, setTwilioOn] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data.items.slice(0, 5))).catch(() => {});
    api.get("/health").then((r) => { setAiOn(r.data.ai); setTwilioOn(r.data.twilio); }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#2D3748]">Overview</h1>
          <p className="text-[#718096] mt-1">Live KPIs across Wazi.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`text-xs px-3 py-1.5 rounded-full font-medium border ${aiOn ? "bg-[#25D366]/10 text-[#16A34A] border-[#25D366]/30" : "bg-[#F7FAFC] text-[#718096] border-[#E2E8F0]"}`} data-testid="ai-status">
            <Sparkles className="w-3 h-3 inline mr-1" /> AI {aiOn ? "active" : "fallback mode"}
          </span>
          <span className={`text-xs px-3 py-1.5 rounded-full font-medium border ${twilioOn ? "bg-[#1E88E5]/10 text-[#1E88E5] border-[#1E88E5]/30" : "bg-[#F7FAFC] text-[#718096] border-[#E2E8F0]"}`} data-testid="twilio-status">
            <MessageCircle className="w-3 h-3 inline mr-1" /> Twilio {twilioOn ? "connected" : "sandbox stub"}
          </span>
        </div>
      </div>

      {stats && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <KPI testid="kpi-workers" icon={Users} label="Active workers" value={stats.active_workers} color="bg-[#25D366]" />
          <KPI testid="kpi-customers" icon={Users} label="Active customers" value={stats.active_customers} color="bg-[#1E88E5]" />
          <KPI testid="kpi-leads" icon={Inbox} label="Leads generated" value={stats.leads_generated} color="bg-[#2D3748]" />
          <KPI testid="kpi-quotes" icon={FileText} label="Quotes sent" value={stats.quotes_sent} color="bg-[#1E88E5]" />
          <KPI testid="kpi-jobs" icon={Briefcase} label="Jobs completed" value={stats.jobs_completed} color="bg-[#16A34A]" />
          <KPI testid="kpi-reviews" icon={Star} label={`Reviews · ⭐ ${stats.avg_rating}`} value={stats.reviews_collected} color="bg-[#FFB400]" />
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl">
          <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <h2 className="font-heading font-semibold text-[#2D3748]">Recent leads</h2>
            <Link to="/admin/leads" className="text-sm text-[#1E88E5] hover:underline" data-testid="see-all-leads">See all</Link>
          </div>
          {leads.length === 0 ? (
            <div className="p-8 text-center text-[#718096] text-sm">No leads yet.</div>
          ) : (
            <ul className="divide-y divide-[#E2E8F0]">
              {leads.map((l) => (
                <li key={l.id} className="px-5 py-4 flex items-start justify-between gap-3" data-testid={`recent-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-[#2D3748] truncate">{l.customer_name} <span className="text-[#718096] font-normal">· {l.location}</span></div>
                    <div className="text-sm text-[#718096] mt-1 line-clamp-2">{l.description}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                    l.status === "completed" ? "bg-[#16A34A]/10 text-[#16A34A]"
                    : l.status === "matched" ? "bg-[#1E88E5]/10 text-[#1E88E5]"
                    : l.status === "quoted" ? "bg-[#FFB400]/10 text-[#FFB400]"
                    : "bg-[#F7FAFC] text-[#718096]"
                  }`}>{l.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <div className="flex items-center gap-2 text-[#25D366] font-heading font-semibold">
            <TrendingUp className="w-5 h-5" /> Growth tips
          </div>
          <ul className="mt-4 space-y-3 text-sm text-[#2D3748]">
            <li>• Share each worker's public profile link to social media.</li>
            <li>• Add Twilio number to start receiving real WhatsApp leads.</li>
            <li>• Plug in EMERGENT_LLM_KEY for AI-drafted quotes & summaries.</li>
            <li>• Onboard 5 painters per area before customer marketing.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
